import React, { useState, useEffect } from 'react';
import { t } from "i18next";
import GlobalModal from "../../../components/UI/Popups/GlobalModal/GlobalModal";
import { calculateProratedAmount } from "../../../services/paymentService";
import { useSelector } from "react-redux";

const ConfirmationModals = ({
    showUpgradeModal,
    showDowngradeModal,
    selectedPlan,
    setShowUpgradeModal,
    setShowDowngradeModal,
    handleProceedUpgrade,
    handleProceedDowngrade,
    handleCloseUpgradeModal,
    activeTab
}) => {
    const [proratedAmount, setProratedAmount] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const user = useSelector(state => state.user.user);

    // Extract numeric price from price string (e.g., "$300 /mo" -> "300")
    const extractPriceValue = (priceString) => {
        if (!priceString) return '';
        const matches = priceString.match(/\$(\d+)/);
        return matches && matches[1] ? matches[1] : '';
    };

    const priceValue = extractPriceValue(selectedPlan?.price);

    // Fetch the prorated amount when the upgrade modal is shown
    useEffect(() => {
        if (showUpgradeModal && selectedPlan?.priceId && user?.stripeSubscriptionId) {
            setIsLoading(true);
            calculateProratedAmount(user.stripeSubscriptionId, selectedPlan.priceId)
                .then(response => {
                    if (response.success) {
                        setProratedAmount(response);
                    }
                })
                .catch(error => {
                    console.error('Error getting prorated amount:', error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setProratedAmount(null);
        }
    }, [showUpgradeModal, selectedPlan, user]);

    // Format the upgrade confirmation message based on whether we have a prorated amount
    const getUpgradeMessage = () => {
        if (isLoading) {
            return t('upgrade.loading_message', 'Calculating your prorated amount...');
        }
        
        if (proratedAmount !== null) {
            // Use the actual prorated amount from Stripe
            const currencySymbol = '$';
            
            // Calculate the immediate charge - this is what the user actually pays
            const immediateCharge = proratedAmount.amount;
            
            // Determine which amount to show based on the active tab
            const isDomesticTab = activeTab === 'domestic';
            const displayAmount = isDomesticTab 
                ? parseFloat(proratedAmount.fullAmount).toFixed(2)
                : Math.abs(parseFloat(immediateCharge - proratedAmount.fullAmount)).toFixed(2);
            
            // Simple message showing just the immediate charge
            return t('upgrade.prorated_message', {
                name: selectedPlan?.name,
                amount: displayAmount,
                currency: currencySymbol,
                defaultValue: `You're about to upgrade your current plan to the {{name}} package. A total charge of {{currency}}{{amount}} will apply, based on your previous payment and any remaining days in your current billing cycle.`
            });
        }
        
        // Fallback to regular message if we couldn't get the prorated amount
        return t('upgrade.confirmation_message', {
            name: selectedPlan?.name,
            price: priceValue,
            defaultValue: `You're about to upgrade your current plan to the {{name}} package. A total charge of ${{priceValue}} will apply.`
        });
    };

    return (
        <>
            {/* Upgrade Confirmation Modal */}
            <GlobalModal
                show={showUpgradeModal}
                onClose={handleCloseUpgradeModal || (() => setShowUpgradeModal(false))}
                title={t('upgrade_subscription')}
                message={getUpgradeMessage()}
                primaryButtonText={t('proceed')}
                secondaryButtonText={t('cancel')}
                primaryButtonAction={handleProceedUpgrade}
                secondaryButtonAction={handleCloseUpgradeModal || (() => setShowUpgradeModal(false))}
                disabled={isLoading}
            />

            {/* Downgrade Confirmation Modal */}
            <GlobalModal
                show={showDowngradeModal}
                onClose={() => setShowDowngradeModal(false)}
                title={selectedPlan?.priceId === 'free'
                    ? t('cancelSubscription.title', "Cancel Subscription")
                    : t('subscription.downgrade', "Downgrade")}
                message={selectedPlan?.priceId === 'free'
                    ? t('cancelSubscription.free_plan_message', `You're about to cancel your subscription. After cancellation, the Free plan will automatically be activated.`)
                    : t('subscription.downgrade_message', {
                        name: selectedPlan?.name
                    })}
                primaryButtonText={t('proceed')}
                secondaryButtonText={t('cancel')}
                primaryButtonAction={handleProceedDowngrade}
                secondaryButtonAction={() => setShowDowngradeModal(false)}
            />
        </>
    );
};

export default ConfirmationModals; 